import { ArrowRight, MapPin } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import LeadDialog from "@/components/site/LeadDialog";
import { LOCATIONS, WHY_ZOLVIX } from "@/data/site";

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHeader
        overline="About"
        title="Technology Built Around Business Outcomes"
        subtitle="Zolvix Technologies is a modern technology company building scalable software, SaaS platforms, automation systems and AI products for ambitious operators."
        testid="about-page-header"
      />

      <section className="py-12 md:py-16">
        <div className="container-zx grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 space-y-5 text-ink-200 text-[15px] leading-relaxed">
            <p>
              We combine deep engineering, business operations expertise and digital innovation to design and build
              systems that deliver measurable impact. Our pods operate as extensions of your team — owning discovery,
              design, build, release and growth as one craft.
            </p>
            <p>
              From regulated industries like manufacturing, infrastructure and oil &amp; gas to digital-native sectors
              like retail and professional services, our work is grounded in domain depth and tied to operating-model outcomes.
            </p>
            <p className="text-sm text-ink-400">
              Zolvix Technologies is part of the broader <a href="https://www.clangrp.com" className="text-cyan hover:text-white" target="_blank" rel="noreferrer">Clan Group</a> ecosystem.
            </p>
            <div className="pt-2">
              <LeadDialog
                source="about_page"
                titleVariant="consultation"
                trigger={
                  <button type="button" className="btn-primary" data-testid="about-cta">
                    Schedule a Consultation <ArrowRight className="h-4 w-4" />
                  </button>
                }
              />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-white/[0.06]">
              <img src="https://images.pexels.com/photos/16018144/pexels-photo-16018144.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Engineering team" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#050F1C]/60 border-y border-white/[0.04]">
        <div className="container-zx">
          <div className="overline mb-3">Where we are</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white">Two hubs. One delivery culture.</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {LOCATIONS.map((l) => (
              <div key={l.city} className="card-surface overflow-hidden">
                <img src={l.image} alt={l.city} className="h-48 w-full object-cover" />
                <div className="p-6">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-cyan" />
                    <div className="font-display text-xl font-semibold text-white">{l.city}, {l.country}</div>
                  </div>
                  <p className="mt-1 text-sm text-ink-400">{l.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-zx">
          <div className="overline mb-3">What we value</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white">A culture that compounds quality</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_ZOLVIX.map((w) => (
              <div key={w.title} className="card-surface p-6">
                <div className="h-10 w-10 rounded-lg grid place-items-center bg-cyan/10 border border-cyan/25 text-cyan">
                  <w.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-white">{w.title}</h3>
                <p className="mt-2 text-sm text-ink-400 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
